import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import CustomerShell from '../../components/customer/CustomerShell';
import { customerV2Api } from '../../lib/api';
import {
  Ticket, Calendar, Clock, MapPin, ChevronLeft, ChevronRight,
  Loader2, ArrowUpRight, Hash, AlertCircle, Sparkles,
} from 'lucide-react';

/**
 * /account/bookings — rebuilt V2 bookings list.
 * Uses /api/v2/bookings (customer-authenticated). Shows upcoming + past,
 * status badges, and "View Ticket" → /tickets/:token for confirmed bookings.
 */

const fmtINR = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;
const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-IN', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch { return iso; }
};
const formatTime = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map((x) => parseInt(x, 10));
  if (isNaN(h)) return hhmm;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m || 0).padStart(2, '0')} ${ampm}`;
};

const statusBadge = (b) => {
  if (b.booking_status === 'confirmed' && b.checked_in) return { label: 'CHECKED IN', tone: 'gray' };
  if (b.booking_status === 'confirmed') return { label: 'CONFIRMED', tone: 'green' };
  if (b.booking_status === 'awaiting_approval') return { label: 'AWAITING VIP APPROVAL', tone: 'amber' };
  if (b.booking_status === 'pending') return { label: 'PENDING', tone: 'amber' };
  if (b.booking_status === 'cancelled') return { label: 'CANCELLED', tone: 'red' };
  if (b.booking_status === 'refunded') return { label: 'REFUNDED', tone: 'red' };
  if (b.booking_status === 'partially_refunded') return { label: 'PARTIALLY REFUNDED', tone: 'amber' };
  return { label: (b.booking_status || '').toUpperCase(), tone: 'gray' };
};

const toneClasses = {
  green: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/40',
  red: 'bg-red-500/15 text-red-300 border-red-400/40',
  gray: 'bg-zinc-500/15 text-zinc-300 border-zinc-400/40',
  amber: 'bg-amber-500/15 text-amber-300 border-amber-400/40',
};

const isUpcoming = (b) => {
  if (!b.event_date) return true;
  try {
    return new Date(b.event_date + 'T00:00:00').getTime() >= new Date().setHours(0, 0, 0, 0);
  } catch { return true; }
};


const BookingCard = ({ booking }) => {
  const navigate = useNavigate();
  const badge = statusBadge(booking);
  const totalPasses = (booking.items || []).reduce((s, i) => s + (i.quantity || 0), 0);
  const canViewTicket = booking.booking_status === 'confirmed' && booking.public_ticket_token;

  return (
    <motion.article
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
      className="booking-card"
      data-testid={`booking-card-${booking.booking_code}`}
    >
      <header className="booking-card-head">
        <div className="flex-1 min-w-0">
          <h3 className="booking-card-title truncate" data-testid={`booking-event-title-${booking.booking_code}`}>
            {booking.event_title}
          </h3>
          <div className="booking-card-meta">
            <span><Calendar size={11} className="text-[var(--accent-gold)]" /> {formatDate(booking.event_date)}</span>
            {booking.event_time && <span><Clock size={11} className="text-[var(--accent-gold)]" /> {formatTime(booking.event_time)}</span>}
            {booking.venue && <span className="truncate"><MapPin size={11} className="text-[var(--accent-gold)]" /> {booking.venue}</span>}
          </div>
        </div>
        <span className={`booking-badge ${toneClasses[badge.tone]}`} data-testid={`booking-status-${booking.booking_code}`}>
          {badge.label}
        </span>
      </header>

      <div className="booking-card-body">
        <div className="booking-card-passes">
          <Ticket size={13} className="text-[var(--accent-gold)] shrink-0" />
          <span className="text-sm text-zinc-300">
            {totalPasses} pass{totalPasses === 1 ? '' : 'es'}
          </span>
          <span className="text-zinc-600 mx-1">·</span>
          <span className="text-zinc-500 text-xs">
            {(booking.items || []).map((i, idx) => (
              <span key={idx}>
                {idx > 0 && ', '}
                {i.pass_type_name} × {i.quantity}
              </span>
            ))}
          </span>
        </div>

        <div className="booking-card-footer">
          <div className="booking-card-code" data-testid={`booking-code-${booking.booking_code}`}>
            <Hash size={11} className="text-zinc-500" />
            <span className="font-mono">{booking.booking_code}</span>
          </div>
          <div className="booking-card-actions">
            <span className="text-zinc-200 font-mono text-sm">{fmtINR(booking.total_amount)}</span>
            {canViewTicket ? (
              <button
                type="button"
                className="booking-view-ticket-btn"
                onClick={() => navigate(`/tickets/${booking.public_ticket_token}`)}
                data-testid={`booking-view-ticket-${booking.booking_code}`}
              >
                View Ticket <ArrowUpRight size={13} />
              </button>
            ) : booking.booking_status === 'pending' ? (
              <span className="text-amber-300 text-xs">Awaiting payment</span>
            ) : booking.booking_status === 'awaiting_approval' ? (
              <span className="text-amber-300 text-xs">Awaiting approval</span>
            ) : null}
          </div>
        </div>
      </div>
    </motion.article>
  );
};


export default function AccountBookingsPage() {
  const [bookings, setBookings] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const LIMIT = 20;

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await customerV2Api.listMyBookings({ skip: (page - 1) * LIMIT, limit: LIMIT });
      setBookings(res.data?.bookings || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      setError(e?.message || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const pages = Math.max(1, Math.ceil(total / LIMIT));
  const upcoming = bookings.filter(isUpcoming);
  const past = bookings.filter((b) => !isUpcoming(b));

  return (
    <CustomerShell title="My Bookings" subtitle="All your reserved nights — past and upcoming">
      <div className="bookings-shell" data-testid="bookings-page">
        {loading ? (
          <div className="events-loading"><Loader2 size={26} className="animate-spin text-[var(--accent-gold)]" /><p>Loading bookings…</p></div>
        ) : error ? (
          <div className="events-error">
            <AlertCircle size={28} className="text-red-400 mb-2" />
            <p className="text-zinc-300">Couldn't load bookings.</p>
            <button className="btn-gold mt-3" onClick={fetchBookings}>Retry</button>
          </div>
        ) : bookings.length === 0 ? (
          <div className="bookings-empty" data-testid="bookings-empty">
            <Sparkles size={36} className="text-[var(--accent-gold)] mb-3" />
            <h3 className="text-lg text-white font-semibold mb-2">No bookings yet</h3>
            <p className="text-sm text-zinc-400 max-w-sm text-center mb-5">
              Browse our upcoming events and reserve your spot. Tickets are limited.
            </p>
            <Link to="/events" className="btn-gold" data-testid="bookings-browse-events">Browse Events</Link>
          </div>
        ) : (
          <>
            {upcoming.length > 0 && (
              <section className="bookings-section" data-testid="bookings-upcoming-section">
                <header className="bookings-section-header">
                  <h2>Upcoming</h2>
                  <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                    {upcoming.length}
                  </span>
                </header>
                <div className="bookings-list">
                  {upcoming.map((b) => <BookingCard key={b._id} booking={b} />)}
                </div>
              </section>
            )}

            {past.length > 0 && (
              <section className="bookings-section" data-testid="bookings-past-section">
                <header className="bookings-section-header">
                  <h2 className="text-zinc-300">Past</h2>
                  <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                    {past.length}
                  </span>
                </header>
                <div className="bookings-list">
                  {past.map((b) => <BookingCard key={b._id} booking={b} />)}
                </div>
              </section>
            )}

            {/* Pagination */}
            {pages > 1 && (
              <div className="bookings-pagination" data-testid="bookings-pagination">
                <button
                  className="bookings-page-btn"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  data-testid="bookings-prev-page"
                >
                  <ChevronLeft size={14} /> Prev
                </button>
                <span className="text-xs text-zinc-500">
                  Page {page} of {pages}
                </span>
                <button
                  className="bookings-page-btn"
                  disabled={page >= pages}
                  onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  data-testid="bookings-next-page"
                >
                  Next <ChevronRight size={14} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </CustomerShell>
  );
}
